import React from 'react';
import { CheckCircle, ArrowRight, Factory, Shield, Clock, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { oemServices } from '../mock/data';

const OEMServices = () => {
  const advantages = [
    {
      icon: <Factory className="h-8 w-8 text-blue-500" />,
      title: "Advanced Automation",
      description: "State-of-the-art machinery and automated systems for cost-effective production"
    },
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Dual Certification",
      description: "Both Halal and Non-Halal certified facilities for diverse market needs"
    },
    {
      icon: <Users className="h-8 w-8 text-purple-500" />,
      title: "R&D Support",
      description: "In-house research and development team for product innovation"
    },
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Quick Turnaround",
      description: "Efficient production processes ensuring fast delivery times"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">OEM Manufacturing Services</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Partner with Singapore's leading food manufacturing group for comprehensive 
              Original Equipment Manufacturing solutions tailored to your business needs.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                End-to-End OEM Capabilities
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Teng Food Holdings offers comprehensive Original Equipment Manufacturing (OEM) 
                  services that transform your food product concepts into market-ready solutions. 
                  With over 20 years of combined experience and state-of-the-art facilities, 
                  we provide the expertise and infrastructure needed to bring your products to life.
                </p>
                <p>
                  From initial product development and formulation to large-scale production and 
                  packaging, our integrated approach ensures quality, consistency, and cost-effectiveness 
                  throughout the entire manufacturing process.
                </p>
              </div>
              
              <div className="mt-8">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Link to="/contact" className="flex items-center">
                    Get OEM Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1651525670114-2b8117390b28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxmb29kJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjAwMDM3NzN8MA&ixlib=rb-4.1.0&q=85"
                alt="OEM Manufacturing"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Advantages */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our OEM Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Leverage our competitive advantages to accelerate your product development 
              and market entry while maintaining the highest quality standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {advantages.map((advantage, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {advantage.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{advantage.title}</h3>
                  <p className="text-gray-600">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detailed Advantages */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Advantages</h3>
                <div className="space-y-3">
                  {oemServices.advantages.map((advantage, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-600">{advantage}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Core Capabilities</h3>
                <div className="space-y-3">
                  {oemServices.capabilities.map((capability, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      <span className="text-gray-600">{capability}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Product Manufacturing Capabilities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We specialize in manufacturing a wide range of food products to meet 
              diverse market demands and customer preferences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {oemServices.productTypes.map((productType, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <Factory className="h-6 w-6 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">{productType}</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Professional manufacturing with quality assurance and regulatory compliance
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our OEM Process
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A streamlined process designed to take your product from concept to market efficiently
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "Understand your requirements and specifications" },
              { step: "02", title: "Development", desc: "Product formulation and prototype creation" },
              { step: "03", title: "Testing", desc: "Quality assurance and compliance verification" },
              { step: "04", title: "Production", desc: "Full-scale manufacturing and packaging" }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h3>
                <p className="text-gray-600">{process.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your OEM Project?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Contact our team today to discuss your manufacturing needs and get a customized quote 
            for your OEM project. Let's bring your food product vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <Link to="/contact" className="flex items-center">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/businesses">Explore Our Facilities</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OEMServices;